// Doctor Instructions (E9 D9): the doctor's written notes, surfaced read-only
// to the nurse on the patient page. PatientDetail loads and orders the notes
// (newest first); this panel decides what each one shows.
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Collapse, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { NURSE_COLORS } from "../theme";
import { parseInstant, formatIstDateTime } from "../../../utils/istDateTime";

// Payload shapes for both doctor-note care plans (manage_care_plan.py
// handle_clinical_notes / handle_notes). One list covers both plans — a field
// absent from a row is skipped, so there is no need to branch on which plan
// produced it.
const CLINICAL_TEXT_FIELDS = [
  // "Note" (template 2)
  ["notes", "Note"],
  ["reason", "Reason"],
  ["category", "Category"],
  // "Clinical Notes" (template 8)
  ["history_of_present_illness", "History of Present Illness"],
  ["co_morbidities", "Co-morbidities"],
  ["past_medical_history", "Past Medical History"],
  ["personal_social_history", "Personal / Social History"],
  // Older rows hold the diagnosis as text; Clinical Notes now stores a list of
  // { provisional_diagnosis, final_diagnosis } (see the table field below).
  ["diagnosis", "Diagnosis"],
];
const CLINICAL_TABLE_FIELDS = [
  ["diagnosis", "Diagnosis",
    (r) => [r.provisional_diagnosis && `Provisional: ${r.provisional_diagnosis}`,
      r.final_diagnosis && `Final: ${r.final_diagnosis}`].filter(Boolean).join(" · ")],
  ["current_medications", "Current Medications",
    (r) => [r.medicine_name, r.frequency, r.quantity].filter(Boolean).join(" · ")],
  ["past_medications", "Past Medications",
    (r) => [r.medicine_name, r.frequency, r.quantity, r.number_of_days && `${r.number_of_days}d`].filter(Boolean).join(" · ")],
  ["past_surgical_history", "Past Surgical History",
    (r) => [r.surgery, r.date, r.remarks].filter(Boolean).join(" · ")],
  ["family_history", "Family History",
    (r) => [r.relation, r.ailment, r.since_date].filter(Boolean).join(" · ")],
];

// When the note was written. The care_plan_data.created_at COLUMN is null on
// every row of the newer care plans (verified in the tenant: Note, Clinical
// Notes, Discharge Advice and Orders all have it unset), so the doctor console
// stamps its own __timeline_created_at inside `data`. Read the column first and
// fall back to that, or a nurse sees undated notes in arbitrary order.
export function noteTimestamp(note) {
  const raw = note?.created_at || note?.data?.__timeline_created_at || note?.data?.__timeline_sort_ts;
  const parsed = parseInstant(raw);
  return parsed ? parsed.getTime() : 0;
}

// "Note" stores category as a LIST (["alert"]) while every other text field is
// a plain string. Rendering the raw array would concatenate the values with no
// separator ("alerturgent"), so normalise to text here. Anything that isn't a
// scalar or an array of scalars is dropped rather than risking "Objects are not
// valid as a React child".
const asText = (v) => {
  if (Array.isArray(v)) return v.filter((x) => x != null && typeof x !== "object").join(", ");
  if (v == null || typeof v === "object") return "";
  return String(v);
};

// What a note has to show, empty values and empty rows already dropped.
function noteContent(note) {
  const d = note?.data || {};
  const texts = CLINICAL_TEXT_FIELDS
    .map(([k, label]) => [k, label, asText(d[k])])
    .filter(([, , value]) => value.trim() !== "");
  const tables = CLINICAL_TABLE_FIELDS
    .map(([k, label, fmt]) => [
      k,
      label,
      (Array.isArray(d[k]) ? d[k] : []).filter((row) => row && typeof row === "object").map(fmt).filter(Boolean),
    ])
    .filter(([, , rows]) => rows.length > 0);
  return { texts, tables };
}

// A category only labels a note ("alert"); with no text beside it there is no
// instruction for the nurse to act on, so on its own it is not content (QA ND
// #2: entries reading just "Category: alert").
const hasInstruction = ({ texts, tables }) =>
  tables.length > 0 || texts.some(([k]) => k !== "category");

function DoctorInstructionEntry({ note, content, open, onToggle }) {
  const author = note?.created_by?.name;
  const ts = noteTimestamp(note);
  // A Date, not the epoch number: parseInstant reads a bare number as text and
  // gives up, which is why these notes used to show no time at all.
  const when = ts ? formatIstDateTime(new Date(ts)) : null;
  const { texts, tables } = content;
  // A one-line preview so a collapsed note still says what it is about.
  const preview = texts[0]?.[2] || tables[0]?.[2]?.[0] || "";
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {(author || when) && (
            <Typography variant="caption" color="text.secondary">
              {[author && `Dr. ${author}`, when].filter(Boolean).join(" · ")}
            </Typography>
          )}
          {!open && preview && (
            <Typography variant="body2" noWrap>{preview}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onToggle} aria-label={open ? "Collapse instruction" : "Expand instruction"}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {texts.map(([k, label, value]) => (
            <Box key={k}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value}</Typography>
            </Box>
          ))}
          {tables.map(([k, label, rows]) => (
            <Box key={`table-${k}`}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {rows.map((row, idx) => (
                  <li key={idx}>
                    <Typography variant="body2" component="span">{row}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function DoctorInstructionsPanel({ notes }) {
  // A note with nothing to show is left out rather than rendered as a blank.
  const entries = useMemo(
    () => (notes || [])
      .map((note) => ({ note, content: noteContent(note) }))
      .filter(({ content }) => hasInstruction(content)),
    [notes]
  );
  // The latest note opens; the rest wait until the nurse asks for them.
  const [open, setOpen] = useState(() => new Set(entries.length ? [0] : []));
  useEffect(() => {
    setOpen(new Set(entries.length ? [0] : []));
  }, [entries]);

  const allOpen = entries.length > 0 && open.size === entries.length;
  const toggle = (i) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    return next;
  });

  return (
    <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <LocalHospitalIcon sx={{ fontSize: 18, color: NURSE_COLORS.primary }} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Doctor Instructions</Typography>
        {entries.length > 1 && (
          <Button
            size="small"
            onClick={() => setOpen(allOpen ? new Set() : new Set(entries.map((_, i) => i)))}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Box>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No doctor instructions recorded.</Typography>
      ) : (
        <Stack spacing={2} divider={<Divider flexItem />}>
          {entries.map(({ note, content }, i) => (
            // care_plan_id is NOT unique — care_plans/ returns no row id, and
            // one admission has many notes under the same plan. The list is
            // rebuilt whole on every load, so its position is a stable key.
            <DoctorInstructionEntry
              key={`${note.care_plan_id ?? "cp"}-${i}`}
              note={note}
              content={content}
              open={open.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
